import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
    Star, 
    Trash2, 
    Search, 
    MessageSquare, 
    Check, 
    X, 
    RefreshCw, 
    AlertCircle, 
    ThumbsUp,
    ThumbsDown,
    Loader2
} from 'lucide-react';

interface Review {
    id: string;
    created_at: string;
    booking_id: string;
    expedition_slug: string;
    rating: number;
    customer_name: string;
    comments: string;
    approved: boolean;
}

const ReviewsManager: React.FC = () => {
    const { theme, expeditions } = useOutletContext<{ theme: 'light' | 'dark'; expeditions: any[] }>();
    const isDark = theme === 'dark';

    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [approvedFilter, setApprovedFilter] = useState<string>('all'); // 'all', 'approved', 'pending'
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Review | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [notification, setNotification] = useState<{ message: string, type: 'error' | 'success' } | null>(null);

    // Auto-hide notification
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Fetch reviews
    const fetchReviews = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('reviews')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setReviews(data || []);
        } catch (err: any) {
            console.error('Error fetching reviews:', err);
            setNotification({
                message: `Error al cargar evaluaciones: ${err.message || err}`,
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    // Toggle Approval Status
    const handleToggleApprove = async (review: Review) => {
        setUpdatingId(review.id);
        const nextStatus = !review.approved;
        try {
            const { error } = await supabase
                .from('reviews')
                .update({ approved: nextStatus })
                .eq('id', review.id);

            if (error) throw error;

            setReviews(prev => prev.map(r => r.id === review.id ? { ...r, approved: nextStatus } : r));
            setNotification({
                message: `Evaluación de ${review.customer_name} ${nextStatus ? 'aprobada y visible' : 'ocultada con éxito'}`,
                type: 'success'
            });
        } catch (err: any) {
            console.error('Error updating review approval:', err);
            setNotification({
                message: `Error al actualizar estado: ${err.message || err}`,
                type: 'error'
            });
        } finally {
            setUpdatingId(null);
        }
    };

    // Delete Review
    const handleDeleteReview = async () => {
        if (!deleteConfirm) return;
        setDeleting(true);
        try {
            const { error } = await supabase
                .from('reviews')
                .delete()
                .eq('id', deleteConfirm.id);

            if (error) throw error;

            setReviews(prev => prev.filter(r => r.id !== deleteConfirm.id));
            setNotification({
                message: `Evaluación eliminada correctamente`,
                type: 'success'
            });
        } catch (err: any) {
            console.error('Error deleting review:', err);
            setNotification({
                message: `Error al eliminar evaluación: ${err.message || err}`,
                type: 'error'
            });
        } finally {
            setDeleting(false);
            setDeleteConfirm(null);
        }
    };

    // Helper to get expedition name
    const getExpeditionName = (slug: string) => {
        const found = expeditions.find(e => e.slug === slug);
        return found ? (found.title_es || found.title) : slug.replace(/-/g, ' ');
    };

    // Filter reviews
    const filteredReviews = reviews.filter(r => {
        const matchesSearch = 
            r.customer_name.toLowerCase().includes(search.toLowerCase()) || 
            (r.comments && r.comments.toLowerCase().includes(search.toLowerCase()));
        
        const matchesApproved = 
            approvedFilter === 'all' ? true :
            approvedFilter === 'approved' ? r.approved : !r.approved;

        return matchesSearch && matchesApproved;
    });

    const cardBg = isDark ? 'bg-[#1a1512]' : 'bg-white';
    const borderColor = isDark ? 'border-rock' : 'border-gray-150';
    const textColor = isDark ? 'text-white' : 'text-gray-900';
    const subTextColor = isDark ? 'text-gray-400' : 'text-gray-500';
    const inputBg = isDark ? 'bg-black text-white' : 'bg-white text-gray-900';

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className={`text-4xl font-title uppercase tracking-tighter ${textColor}`}>Testimonios y Reseñas</h1>
                    <p className={`${subTextColor} text-[10px] uppercase tracking-[0.3em] font-bold mt-1`}>Moderación y visualización de la retroalimentación de clientes.</p>
                </div>
                <button 
                    onClick={fetchReviews} 
                    disabled={loading}
                    className={`px-6 py-3 border ${borderColor} text-[10px] font-black uppercase tracking-widest transition-all rounded-[10px] flex items-center gap-2 hover:bg-[#00d4ff] hover:text-black hover:border-[#00d4ff] shadow-sm disabled:opacity-50 ${textColor}`}
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                </button>
            </div>

            {/* Notification Alert */}
            {notification && (
                <div className={`p-4 rounded-[10px] flex items-center gap-3 border animate-in slide-in-from-top duration-300 ${
                    notification.type === 'error' 
                        ? 'bg-red-500/10 border-red-500/30 text-red-500' 
                        : 'bg-[#25D366]/10 border-[#25D366]/30 text-[#25D366]'
                }`}>
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-wider">{notification.message}</span>
                </div>
            )}

            {/* Search and Filters */}
            <div className={`${cardBg} border ${borderColor} p-6 rounded-[10px] shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4`}>
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input 
                        type="text"
                        placeholder="Buscar por cliente o comentarios..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={`w-full pl-12 pr-4 py-3.5 text-xs rounded-[8px] border ${borderColor} outline-none focus:border-[#00d4ff] ${inputBg}`}
                    />
                </div>
                <div>
                    <select
                        value={approvedFilter}
                        onChange={(e) => setApprovedFilter(e.target.value)}
                        className={`w-full px-4 py-3.5 text-xs rounded-[8px] border ${borderColor} outline-none focus:border-[#00d4ff] uppercase tracking-wider font-bold ${inputBg}`}
                    >
                        <option value="all">TODOS LOS ESTADOS</option>
                        <option value="approved">APROBADOS / VISIBLES</option>
                        <option value="pending">OCULTOS / PENDIENTES</option>
                    </select>
                </div>
                <div className="flex items-center justify-end text-xs font-bold text-gray-500 uppercase tracking-widest">
                    <span>{filteredReviews.length} Reseñas encontradas</span>
                </div>
            </div>

            {/* Reviews List */}
            {loading ? (
                <div className="py-24 text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-[#00d4ff] mx-auto mb-4" />
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Cargando evaluaciones...</span>
                </div>
            ) : filteredReviews.length === 0 ? (
                <div className={`p-16 text-center border border-dashed ${borderColor} rounded-[10px]`}>
                    <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4 opacity-50" />
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500">No se encontraron reseñas registradas.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {filteredReviews.map((review) => {
                        const isApproved = review.approved;
                        return (
                            <div 
                                key={review.id} 
                                className={`${cardBg} border ${
                                    isApproved ? (isDark ? 'border-[#25D366]/20' : 'border-[#25D366]/30') : borderColor
                                } p-4 md:p-5 rounded-[8px] shadow-sm relative group flex flex-col md:flex-row md:items-start justify-between gap-4 transition-all duration-300 hover:shadow-md`}
                            >
                                <div className="space-y-2 flex-1">
                                    {/* Top Metadata */}
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <h4 className={`text-sm font-bold uppercase tracking-wide ${textColor}`}>
                                            {review.customer_name}
                                        </h4>
                                        <span className="w-1 h-1 bg-gray-600 rounded-full"></span>
                                        <span className="text-[9px] text-[#00d4ff] font-bold uppercase tracking-widest">
                                            {getExpeditionName(review.expedition_slug)}
                                        </span>
                                        <span className="w-1.5 h-1.5 bg-gray-600 rounded-full"></span>
                                        <span className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">
                                            {new Date(review.created_at).toLocaleDateString('es-CL', {
                                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                            })}
                                        </span>
                                    </div>

                                    {/* Stars Display */}
                                    <div className="flex items-center gap-0.5">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star 
                                                key={i} 
                                                className={`w-3.5 h-3.5 ${
                                                    i < review.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-700'
                                                }`} 
                                            />
                                        ))}
                                    </div>

                                    {/* Comments Block */}
                                    {review.comments ? (
                                        <div className={`p-3 rounded-[6px] text-xs leading-normal whitespace-pre-line font-medium ${
                                            isDark ? 'bg-black/50 text-gray-300' : 'bg-gray-50 text-gray-700'
                                        }`}>
                                            {review.comments}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-500 italic">El explorador no incluyó comentarios escritos.</p>
                                    )}
                                </div>

                                {/* Right Side Actions (Modulation) */}
                                <div className="flex md:flex-col items-center gap-2 shrink-0 self-end md:self-start">
                                    <button
                                        type="button"
                                        disabled={updatingId === review.id}
                                        onClick={() => handleToggleApprove(review)}
                                        className={`w-24 py-2 rounded-[6px] text-[8px] font-black uppercase tracking-wider flex items-center justify-center gap-1 border transition-all ${
                                            isApproved 
                                                ? 'bg-[#25D366]/10 border-[#25D366]/30 text-[#25D366] hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-500'
                                                : 'bg-orange-500/10 border-orange-500/30 text-orange-500 hover:bg-[#25D366]/10 hover:border-[#25D366]/30 hover:text-[#25D366]'
                                        }`}
                                    >
                                        {updatingId === review.id ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : isApproved ? (
                                            <>
                                                <ThumbsUp className="w-2.5 h-2.5" />
                                                Aprobado
                                            </>
                                        ) : (
                                            <>
                                                <ThumbsDown className="w-2.5 h-2.5" />
                                                Oculto
                                            </>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setDeleteConfirm(review)}
                                        className={`p-2 rounded-[6px] border ${borderColor} hover:border-red-500 hover:bg-red-500/10 text-gray-500 hover:text-red-500 transition-all`}
                                        title="Eliminar Reseña"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-[11000] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
                    <div className={`relative max-w-md w-full ${cardBg} border ${borderColor} p-8 rounded-[12px] shadow-2xl space-y-6 text-center animate-in zoom-in-95 duration-200`}>
                        <Trash2 className="w-12 h-12 text-red-500 mx-auto" />
                        <div className="space-y-2">
                            <h3 className={`text-xl font-title uppercase ${textColor}`}>¿Eliminar Testimonio?</h3>
                            <p className="text-xs text-gray-500 leading-relaxed uppercase tracking-wider">
                                Esta acción eliminará permanentemente la reseña de <strong className={textColor}>{deleteConfirm.customer_name}</strong> de la base de datos de forma irreversible.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3 pt-2">
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={() => setDeleteConfirm(null)}
                                className={`py-4 border ${borderColor} rounded-[8px] text-[10px] font-black uppercase tracking-widest ${textColor} hover:bg-white/5 transition-all`}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={handleDeleteReview}
                                className="py-4 bg-red-500 hover:bg-red-600 text-white rounded-[8px] text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all"
                            >
                                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Sí, Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewsManager;
